import adminApi, {
  BillingTransactionRecord,
  BillingUsageStatus,
  PlanLimitStatus,
  WhatsAppMessageUsage,
  WhatsAppUsageVerification,
} from '@/services/admin.api';
import { useAuth } from '@/hooks/useAuth';
import { usePlanGate } from '@/hooks/usePlanGate';
import { TenantFeatures } from '@/store/slices/authSlice';
import {
  AccountTree,
  // Api, // unused now that API calls / AI tokens tiles are commented out
  Cable,
  Campaign,
  Check,
  CheckCircle,
  CheckCircleOutline,
  ChevronLeft,
  ChevronRight,
  Contacts,
  Diamond,
  ExpandMore,
  Forum,
  Group,
  Lock,
  Receipt,
  SmartToy,
  PermMedia,
  // Storage, // unused now that the Storage usage tile is commented out
  ToggleOn,
  // Tune, // unused now that the "AI & RAG" plan-details group is commented out
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import StatusChip from '@/components/ui/StatusChip';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type SubscriptionPlanFeatures = {
  messagesLimit: number;
  storageLimit: number;
  apiCallsLimit: number;
  usersLimit: number;
  agentsLimit: number;
  whatsappEnabled: boolean;
  llmConfigEnabled: boolean;
  customBranding: boolean;
  prioritySupport: boolean;
  sslEnabled: boolean;
  apiAccess: boolean;
  webhooksEnabled: boolean;
  campaignsEnabled: boolean;
  whatsapp24hMessageLimit: number;
  campaignMaxRecipientsDefault: number;
  campaignDailyLimitDefault: number;
  campaignBatchSize: number;
  campaignBatchDelayMs: number;
  aiMaxTokensPerResponse: number;
  ragTopK: number;
  ragTopKRerank: number;
  ragScoreThreshold: number;
  contactMaxCustomAttributes: number;
  contactMaxAttributeKeyLength: number;
  contactMaxAttributeValueLength: number;
  contactCsvImportMaxRows: number;
  externalApiBulkUpsertMaxContacts: number;
  externalApiDefaultRateLimitPerMinute: number;
  externalApiDefaultBurstAllowance: number;
  externalApiKeyMaxFailedAttempts: number;
  externalApiKeyLockoutMs: number;
  workflowMaxConcurrentExecutionsDefault: number;
  workflowDeduplicationWindowSecondsDefault: number;
  workflowMaxRetriesDefault: number;
  workflowMaxNestingDepth: number;
  templateStatusHistoryLimit: number;
  maxUploadBodySizeMb: number;
  instagramEnabled?: boolean;
  workflowsEnabled?: boolean;
  enableRag?: boolean;
  rolesLimit?: number;
  contactsLimit?: number;
  aiTokensLimit?: number;
  leadMaxCustomAttributes?: number;
  leadMaxAttributeKeyLength?: number;
  leadMaxAttributeValueLength?: number;
  externalApiBulkUpsertMaxLeads?: number;
};

type SubscriptionPlan = {
  _id?: string;
  name: string;
  displayName: string;
  description: string;
  price: number;
  yearlyPrice?: number;
  currency: string;
  billingCycle: 'monthly' | 'yearly' | 'both';
  features: SubscriptionPlanFeatures;
  isActive: boolean;
  isPopular?: boolean;
  sortOrder: number;
};

declare global {
  interface Window {
    Cashfree?: (opts: { mode: 'sandbox' | 'production' }) => {
      checkout: (opts: { paymentSessionId: string; redirectTarget?: '_self' | '_blank' | '_modal' }) => Promise<void> | void;
    };
  }
}

const CASHFREE_ENV: 'sandbox' | 'production' =
  import.meta.env['VITE_CASHFREE_ENV'] === 'production' ? 'production' : 'sandbox';
const CHECKOUT_ORDER_ID_PARAM = 'order_id';
/** How many times to poll confirmCheckoutOrder while the webhook may still be
 *  in flight, before giving up with a "we'll confirm shortly" message. */
const CONFIRM_POLL_ATTEMPTS = 5;
const CONFIRM_POLL_DELAY_MS = 3000;

// ─── Formatting helpers ───────────────────────────────────────────────────────

// Re-enabled for the Customer-media usage tile. The Storage tile that
// originally used this stays hidden; this helper is shared, not storage-specific.
// Sub-GB values read as MB — "0.0 GB" beside a real 40 MB of attachments looks
// like a bug, and small tenants sit in that range for a long time.
function bytesToGB(bytes: number): string {
  if (bytes < 1024 ** 3) return `${Math.round(bytes / 1024 ** 2)} MB`;
  const gb = bytes / 1024 ** 3;
  if (gb >= 10) return `${Math.round(gb)} GB`;
  return `${gb.toFixed(1)} GB`;
}

function formatMoney(n: number): string {
  return Math.round(n).toLocaleString();
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** "Oct 1, 2026" — the date half of a timestamp, for rows that show time on a second line. */
function formatDay(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "1:10 PM" — the time half. */
function formatClock(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatDurationMs(ms: number): string {
  if (ms <= 0) return '0 ms';
  if (ms < 1000) return `${ms.toLocaleString()} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${Number(seconds.toFixed(1))}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.round(hours)} hr`;
  return `${Math.round(hours / 24)} d`;
}

function formatDurationSeconds(seconds: number): string {
  return formatDurationMs(seconds * 1000);
}

/** YYYY-MM-DD for the LOCAL calendar day — `toISOString().slice(0, 10)` would
 *  hand back the UTC day, which is yesterday for anyone west of Greenwich in
 *  the evening and would silently shift every preset range by a day. */
function toDateInput(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** The billing period boundaries arrive as full ISO timestamps. We take the
 *  date part verbatim rather than re-deriving it through a local `Date`, so
 *  the cycle we ask the API for is exactly the cycle the API stored. */
function isoToDateInput(iso?: string): string {
  if (!iso) return '';
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  if (match?.[1]) return match[1];
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? '' : toDateInput(parsed);
}

/** "2026-08-01" → "1 Aug". Built from the parts, not `new Date(str)`, because
 *  a bare YYYY-MM-DD string is parsed as UTC midnight and would render as the
 *  previous day in negative-offset timezones. */
function formatDayShort(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return dateStr;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });
}

/** Same, with the year — used in the per-day table where rows can straddle
 *  a year boundary on a custom range. */
function formatDayLong(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return dateStr;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

/** Local calendar day N days back, inclusive of today (7 → today + 6 before it). */
function daysAgoDateInput(days: number): string {
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  return toDateInput(start);
}

/** Billing status → StatusChip tone (see components/ui/StatusChip palette) + customer-facing label. */
const TRANSACTION_STATUS_CHIP: Record<BillingTransactionRecord['status'], { tone: string; label: string }> = {
  captured: { tone: 'success', label: 'Paid' },
  failed: { tone: 'error', label: 'Failed' },
  expired: { tone: 'inactive', label: 'Expired' },
  refunded: { tone: 'info', label: 'Refunded' },
  created: { tone: 'warning', label: 'Pending' },
  pending: { tone: 'warning', label: 'Pending' },
  authorized: { tone: 'warning', label: 'Authorized' },
};

const RESOLVED_VIA_LABELS: Record<NonNullable<BillingTransactionRecord['resolvedVia']>, string> = {
  webhook: 'Webhook',
  redirect: 'Checkout redirect',
  reconciliation: 'Reconciliation sweep',
};

/** Cycle-aware price for a plan. Returns `Free` for zero-cost plans. */
function getCyclePrice(
  plan: SubscriptionPlan,
  cycle: 'monthly' | 'yearly'
): { amount: string; suffix?: string; sub?: string } {
  const currency = plan.currency || 'INR';
  const yearlyPrice = plan.yearlyPrice ?? plan.price * 12;

  if (plan.price <= 0 && yearlyPrice <= 0) {
    return { amount: 'Free' };
  }

  const supportsMonthly = plan.billingCycle === 'monthly' || plan.billingCycle === 'both';
  const supportsYearly = plan.billingCycle === 'yearly' || plan.billingCycle === 'both';
  const effectiveCycle: 'monthly' | 'yearly' =
    cycle === 'yearly' ? (supportsYearly ? 'yearly' : 'monthly') : supportsMonthly ? 'monthly' : 'yearly';

  if (effectiveCycle === 'yearly') {
    return {
      amount: `${currency} ${formatMoney(yearlyPrice)}`,
      suffix: '/year',
      sub: `≈ ${currency} ${formatMoney(yearlyPrice / 12)}/mo`,
    };
  }

  return { amount: `${currency} ${formatMoney(plan.price)}`, suffix: '/month' };
}

// ─── Full plan-detail field config ────────────────────────────────────────────

type FeatureFieldKind = 'count' | 'ms' | 'seconds' | 'mb' | 'score';

interface FeatureFieldConfig {
  key: keyof SubscriptionPlanFeatures;
  label: string;
  kind: FeatureFieldKind;
  /** Matches the SubscriptionPlan model's own "0 = unbounded" convention for this field. */
  unbounded?: boolean;
}

interface FeatureGroupConfig {
  title: string;
  icon: React.ReactNode;
  fields: FeatureFieldConfig[];
}

function renderFeatureFieldValue(features: SubscriptionPlanFeatures, field: FeatureFieldConfig): string {
  const raw = Number(features[field.key]) || 0;
  if (field.unbounded && raw <= 0) return 'Unlimited';
  switch (field.kind) {
    case 'ms':
      return formatDurationMs(raw);
    case 'seconds':
      return formatDurationSeconds(raw);
    case 'mb':
      return `${raw.toLocaleString()} MB`;
    case 'score':
      return raw.toFixed(2);
    default:
      return Math.round(raw).toLocaleString();
  }
}

const BOOLEAN_FEATURE_FIELDS: Array<{ key: keyof SubscriptionPlanFeatures; label: string }> = [
  { key: 'whatsappEnabled', label: 'WhatsApp' },
  { key: 'instagramEnabled', label: 'Instagram' },
  { key: 'campaignsEnabled', label: 'Campaigns' },
  { key: 'workflowsEnabled', label: 'Automation Workflows' },
  { key: 'enableRag', label: 'AI Knowledge Base (RAG)' },
  { key: 'llmConfigEnabled', label: 'LLM Configuration' },
  { key: 'apiAccess', label: 'API Access' },
  { key: 'webhooksEnabled', label: 'Webhooks' },
  { key: 'prioritySupport', label: 'Priority Support' },
  { key: 'customBranding', label: 'Custom Branding' },
  { key: 'sslEnabled', label: 'SSL' },
];

const NUMERIC_FEATURE_GROUPS: FeatureGroupConfig[] = [
  {
    title: 'WhatsApp & Campaigns',
    icon: <Campaign fontSize="small" color="action" />,
    fields: [
      { key: 'whatsapp24hMessageLimit', label: '24h Message Limit', kind: 'count', unbounded: true },
      { key: 'campaignMaxRecipientsDefault', label: 'Max Recipients / Campaign', kind: 'count' },
      { key: 'campaignDailyLimitDefault', label: 'Daily Campaign Limit', kind: 'count', unbounded: true },
      { key: 'campaignBatchSize', label: 'Batch Size', kind: 'count' },
      { key: 'campaignBatchDelayMs', label: 'Batch Delay', kind: 'ms' },
      { key: 'templateStatusHistoryLimit', label: 'Template Status History', kind: 'count' },
    ],
  },
  // Hidden for now — commented out, not deleted, per request to keep AI & RAG out of Plan Details UI.
  // {
  //   title: 'AI & RAG',
  //   icon: <Tune fontSize="small" color="action" />,
  //   fields: [
  //     { key: 'aiMaxTokensPerResponse', label: 'Max Tokens / Response', kind: 'count' },
  //     { key: 'ragTopK', label: 'RAG Top K', kind: 'count' },
  //     { key: 'ragTopKRerank', label: 'RAG Rerank Top K', kind: 'count' },
  //     { key: 'ragScoreThreshold', label: 'RAG Score Threshold', kind: 'score' },
  //   ],
  // },
  {
    title: 'Contacts & Leads',
    icon: <Contacts fontSize="small" color="action" />,
    fields: [
      { key: 'contactMaxCustomAttributes', label: 'Contact Max Custom Attributes', kind: 'count' },
      { key: 'contactMaxAttributeKeyLength', label: 'Contact Attr Key Max Length', kind: 'count' },
      { key: 'contactMaxAttributeValueLength', label: 'Contact Attr Value Max Length', kind: 'count' },
      { key: 'contactCsvImportMaxRows', label: 'CSV Import Max Rows', kind: 'count' },
      { key: 'leadMaxCustomAttributes', label: 'Lead Max Custom Attributes', kind: 'count' },
      { key: 'leadMaxAttributeKeyLength', label: 'Lead Attr Key Max Length', kind: 'count' },
      { key: 'leadMaxAttributeValueLength', label: 'Lead Attr Value Max Length', kind: 'count' },
    ],
  },
  {
    title: 'External API',
    icon: <Cable fontSize="small" color="action" />,
    fields: [
      { key: 'externalApiBulkUpsertMaxContacts', label: 'Bulk Upsert Max Contacts', kind: 'count' },
      { key: 'externalApiBulkUpsertMaxLeads', label: 'Bulk Upsert Max Leads', kind: 'count' },
      { key: 'externalApiDefaultRateLimitPerMinute', label: 'Rate Limit (/min)', kind: 'count' },
      { key: 'externalApiDefaultBurstAllowance', label: 'Burst Allowance', kind: 'count' },
      { key: 'externalApiKeyMaxFailedAttempts', label: 'Max Failed Attempts', kind: 'count' },
      { key: 'externalApiKeyLockoutMs', label: 'Lockout Duration', kind: 'ms' },
    ],
  },
  {
    title: 'Workflows & Uploads',
    icon: <AccountTree fontSize="small" color="action" />,
    fields: [
      { key: 'workflowMaxConcurrentExecutionsDefault', label: 'Max Concurrent Executions', kind: 'count' },
      { key: 'workflowDeduplicationWindowSecondsDefault', label: 'Deduplication Window', kind: 'seconds' },
      { key: 'workflowMaxRetriesDefault', label: 'Max Retries', kind: 'count' },
      { key: 'workflowMaxNestingDepth', label: 'Max Nesting Depth', kind: 'count' },
      { key: 'maxUploadBodySizeMb', label: 'Max Upload Size', kind: 'mb' },
    ],
  },
];

const accordionSx = {
  mb: 1.5,
  borderRadius: 2.5,
  borderColor: 'divider',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { margin: '0 0 12px' },
  '& .MuiAccordionSummary-root': { px: 2.5, minHeight: 56 },
  '& .MuiAccordionSummary-content': { my: 1.25 },
  '& .MuiAccordionDetails-root': { px: 2.5, pt: 0, pb: 1 },
} as const;

/** A single label → value row with a hairline separator, used across all detail groups. */
const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    spacing={2}
    sx={{ py: 1.25, borderBottom: (theme) => `1px dashed ${theme.palette.divider}` }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ flexShrink: 0, textAlign: 'right' }}>{value}</Box>
  </Stack>
);

/**
 * Soft, legible Yes/No badge for boolean capabilities. When a feature is not
 * included (`enabled: false`) and an `onUpgradeClick` handler is supplied, the
 * badge becomes a clickable "locked" chip that opens the plan-upgrade dialog
 * instead of a dead-end "No" label.
 */
const BoolBadge: React.FC<{ enabled: boolean; onUpgradeClick?: () => void }> = ({
  enabled,
  onUpgradeClick,
}) => (
  <Chip
    size="small"
    clickable={!enabled && Boolean(onUpgradeClick)}
    {...(!enabled && onUpgradeClick ? { onClick: onUpgradeClick } : {})}
    {...(enabled
      ? { icon: <Check sx={{ fontSize: 15 }} /> }
      : onUpgradeClick
        ? { icon: <Lock sx={{ fontSize: 13 }} /> }
        : {})}
    label={enabled ? 'Yes' : onUpgradeClick ? 'Upgrade' : 'No'}
    sx={(theme) =>
      enabled
        ? {
            height: 24,
            fontWeight: 700,
            bgcolor: alpha(theme.palette.success.main, 0.16),
            color: theme.palette.success.dark,
            '& .MuiChip-icon': { color: theme.palette.success.dark, ml: 0.5, mr: -0.25 },
          }
        : {
            height: 24,
            fontWeight: 700,
            bgcolor: alpha(theme.palette.text.primary, 0.06),
            color: theme.palette.text.disabled,
            '& .MuiChip-icon': { color: theme.palette.text.disabled, ml: 0.5, mr: -0.25 },
            ...(onUpgradeClick && {
              cursor: 'pointer',
              '&:hover': {
                bgcolor: alpha(theme.palette.warning.main, 0.14),
                color: theme.palette.warning.dark,
                '& .MuiChip-icon': { color: theme.palette.warning.dark },
              },
            }),
          }
    }
  />
);

const FeatureGroupAccordion: React.FC<{
  group: FeatureGroupConfig;
  features: SubscriptionPlanFeatures;
  defaultExpanded?: boolean;
}> = ({ group, features, defaultExpanded = false }) => (
  <Accordion defaultExpanded={defaultExpanded} disableGutters variant="outlined" sx={accordionSx}>
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        {group.icon}
        <Typography variant="subtitle2" fontWeight={700}>
          {group.title}
        </Typography>
      </Stack>
    </AccordionSummary>
    <AccordionDetails>
      <Grid container columnSpacing={4} rowSpacing={0}>
        {group.fields.map((field) => (
          <Grid item xs={12} sm={6} key={String(field.key)}>
            <DetailRow
              label={field.label}
              value={
                <Typography variant="body2" fontWeight={700}>
                  {renderFeatureFieldValue(features, field)}
                </Typography>
              }
            />
          </Grid>
        ))}
      </Grid>
    </AccordionDetails>
  </Accordion>
);

const CoreFeaturesAccordion: React.FC<{ features: SubscriptionPlanFeatures }> = ({ features }) => {
  const { guard } = usePlanGate();

  return (
    <Accordion defaultExpanded disableGutters variant="outlined" sx={accordionSx}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <ToggleOn fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={700}>
            Core Features
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container columnSpacing={4} rowSpacing={0}>
          {BOOLEAN_FEATURE_FIELDS.map((field) => {
            const enabled = Boolean(features[field.key]);
            return (
              <Grid item xs={12} sm={6} key={String(field.key)}>
                <DetailRow
                  label={field.label}
                  value={
                    <BoolBadge
                      enabled={enabled}
                      {...(!enabled && {
                        onUpgradeClick: guard(field.key as keyof TenantFeatures, () => {}),
                      })}
                    />
                  }
                />
              </Grid>
            );
          })}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

// ─── Comparison-card feature list ─────────────────────────────────────────────

function getIncludedFeatures(
  features: SubscriptionPlan['features'],
  options: { includeMeteredLimits?: boolean } = {}
): Array<{ label: string; value: string }> {
  const { includeMeteredLimits = true } = options;
  const included: Array<{ label: string; value: string }> = [];

  // Numeric limits already shown live (with usage) in the meters section above, for the
  // active plan only — comparison cards still need them for side-by-side comparison.
  if (includeMeteredLimits) {
    included.push({ label: 'Messages quota', value: `${features.messagesLimit.toLocaleString()}` });
    // Hidden for now — commented out, not deleted, per request.
    // included.push({ label: 'Storage limit', value: bytesToGB(features.storageLimit) });
    // included.push({ label: 'API calls limit', value: `${features.apiCallsLimit.toLocaleString()}` });
    included.push({ label: 'Users limit', value: `${features.usersLimit.toLocaleString()}` });
    included.push({ label: 'Agents limit', value: `${features.agentsLimit.toLocaleString()}` });
  }

  // Booleans
  if (features.whatsappEnabled) included.push({ label: 'WhatsApp integration', value: 'Included' });
  if (features.llmConfigEnabled) included.push({ label: 'LLM configuration', value: 'Included' });
  if (features.customBranding) included.push({ label: 'Custom branding', value: 'Included' });
  if (features.prioritySupport) included.push({ label: 'Priority support', value: 'Included' });
  if (features.sslEnabled) included.push({ label: 'SSL enabled', value: 'Included' });
  if (features.apiAccess) included.push({ label: 'API access', value: 'Included' });
  if (features.webhooksEnabled) included.push({ label: 'Webhooks', value: 'Included' });

  return included;
}

// ─── Usage meters ─────────────────────────────────────────────────────────────

// Usage meters use a calm blue as the default (well within limits), shifting to a tuned
// amber only when nearing the cap and a soft red once exceeded — a quieter, more modern
// ramp than MUI's default green/yellow/red.
type MeterTone = 'normal' | 'warning' | 'exceeded';

const METER_TONES: Record<MeterTone, { bar: string; label: string }> = {
  normal: { bar: '#2563EB', label: '#1D4ED8' }, // blue-600 / blue-700
  warning: { bar: '#D97706', label: '#B45309' }, // amber-600 / amber-700
  exceeded: { bar: '#E11D48', label: '#BE123C' }, // rose-600 / rose-700
};

function getMeterTone(status: PlanLimitStatus): MeterTone {
  if (status.exceeded) return 'exceeded';
  if (!status.unbounded && status.usagePercent >= 80) return 'warning';
  return 'normal';
}

const integerFormatter = (n: number) => Math.round(n).toLocaleString();

const UsageTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  status: PlanLimitStatus;
  formatter?: (n: number) => string;
  /** Optional line under the meter explaining what hitting the ceiling
   *  actually DOES. Most quotas simply block the next action, so the number
   *  speaks for itself; inbound media instead degrades silently (previews
   *  only), which a bare red bar would not communicate. */
  note?: string;
  /** Optional remedy link, shown only when the meter is actually under
   *  pressure — a cleanup shortcut on a healthy tile is noise. */
  action?: { label: string; to: string };
}> = ({ icon, label, status, formatter = integerFormatter, note, action }) => {
  const tone = getMeterTone(status);
  const toneColor = METER_TONES[tone];
  const percent = status.unbounded ? 100 : Math.min(Math.round(status.usagePercent), 100);
  return (
    <Box
      sx={(theme) => ({
        p: 2.5,
        height: '100%',
        borderRadius: 2.5,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'border-color .2s, box-shadow .2s',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.35),
          boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.05)}`,
        },
      })}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} mb={1.75}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 1.5,
              color: 'text.secondary',
              bgcolor: alpha(theme.palette.text.primary, 0.05),
            })}
          >
            {icon}
          </Box>
          <Typography variant="body2" fontWeight={700}>
            {label}
          </Typography>
        </Stack>
        {status.unbounded ? (
          <Chip
            size="small"
            label="Unlimited"
            sx={(theme) => ({
              height: 22,
              fontWeight: 700,
              bgcolor: alpha(theme.palette.text.primary, 0.06),
              color: 'text.secondary',
            })}
          />
        ) : (
          <Chip
            size="small"
            label={status.exceeded ? 'Over limit' : `${percent}%`}
            sx={{
              height: 22,
              fontWeight: 700,
              bgcolor: alpha(toneColor.bar, 0.14),
              color: toneColor.label,
            }}
          />
        )}
      </Stack>

      <Stack direction="row" alignItems="baseline" spacing={0.5} mb={1.5}>
        <Typography variant="h6" fontWeight={800} lineHeight={1}>
          {formatter(status.used)}
        </Typography>
        {!status.unbounded && (
          <Typography variant="body2" color="text.secondary">
            / {formatter(status.limit)}
          </Typography>
        )}
      </Stack>

      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{
          height: 8,
          borderRadius: 999,
          opacity: status.unbounded ? 0.35 : 1,
          bgcolor: alpha(toneColor.bar, 0.14),
          '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: toneColor.bar },
        }}
      />
      {/* A tile-specific `note` replaces the generic caption rather than
          stacking with it — "Limit reached" plus a second sentence saying the
          feature still partly works reads as contradictory. */}
      {note ? (
        <>
          <Typography
            variant="caption"
            sx={{
              mt: 1,
              display: 'block',
              color: status.exceeded ? toneColor.label : 'text.secondary',
            }}
          >
            {note}
          </Typography>
          {action && (
            <Button
              size="small"
              href={action.to}
              sx={{ mt: 0.5, px: 0, minWidth: 0, fontSize: '0.72rem' }}
            >
              {action.label}
            </Button>
          )}
        </>
      ) : (
        status.exceeded && (
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: toneColor.label }}>
            Limit reached — upgrade for more headroom.
          </Typography>
        )
      )}
    </Box>
  );
};

// ─── Comparison plan card ─────────────────────────────────────────────────────

/** URL slugs for the billing tabs, in Tabs order. Index 0 is the default and
 *  is represented by the absence of a `tab` param. */
const TAB_SLUGS: string[] = ['usage', 'details', 'history', 'compare', 'whatsapp'];

/** Index of the WhatsApp tab in TAB_SLUGS / the Tabs children. Named because
 *  the lazy fetch below keys off it and a bare `4` would rot the moment a tab
 *  is inserted. */
const WHATSAPP_TAB_INDEX = 4;

/** Flat counter card for the WhatsApp tab. Deliberately NOT UsageTile: these
 *  are billed-message counts with no plan ceiling to meter against, so a
 *  progress bar would invent a limit that doesn't exist. */
const WhatsAppStatCard: React.FC<{
  label: string;
  value: string;
  caption: string;
  highlight?: boolean;
}> = ({ label, value, caption, highlight = false }) => (
  <Box
    sx={(theme) => ({
      p: 2.5,
      height: '100%',
      borderRadius: 2.5,
      border: `1px solid ${
        highlight ? alpha(theme.palette.primary.main, 0.35) : theme.palette.divider
      }`,
      bgcolor: highlight ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
      transition: 'border-color .2s, box-shadow .2s',
      '&:hover': {
        borderColor: alpha(theme.palette.primary.main, 0.35),
        boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.05)}`,
      },
    })}
  >
    <Typography
      variant="body2"
      fontWeight={700}
      sx={{ color: highlight ? 'primary.main' : 'text.primary' }}
    >
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={800} lineHeight={1} mt={1.25}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
      {caption}
    </Typography>
  </Box>
);

const PlanCompareCard: React.FC<{
  plan: SubscriptionPlan;
  billingCycle: 'monthly' | 'yearly';
  isCurrent: boolean;
  isUpgrade: boolean;
  isPaying: boolean;
  onUpgrade: () => void;
}> = ({ plan, billingCycle, isCurrent, isUpgrade, isPaying, onUpgrade }) => {
  const features = getIncludedFeatures(plan.features);
  const price = getCyclePrice(plan, billingCycle);
  const canPay = isUpgrade && plan.price > 0;

  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        position: 'relative',
        overflow: 'visible',
        transition: 'border-color .2s, box-shadow .2s, transform .2s',
        ...(isCurrent
          ? {
              borderColor: theme.palette.primary.main,
              borderWidth: 2,
              boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.18)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.03),
            }
          : {
              '&:hover': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
                boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.06)}`,
                transform: 'translateY(-2px)',
              },
            }),
      })}
    >
      {(isCurrent || plan.isPopular) && (
        <Chip
          size="small"
          label={isCurrent ? 'Current plan' : 'Popular'}
          color={isCurrent ? 'primary' : 'secondary'}
          sx={{
            position: 'absolute',
            top: -12,
            left: 20,
            fontWeight: 700,
            height: 24,
            boxShadow: 1,
          }}
        />
      )}
      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <Typography variant="h6" fontWeight={800}>
          {plan.displayName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2, minHeight: 40 }}>
          {plan.description}
        </Typography>

        <Stack direction="row" alignItems="baseline" spacing={0.75} flexWrap="wrap">
          <Typography variant="h4" fontWeight={800} lineHeight={1.1}>
            {price.amount}
          </Typography>
          {price.suffix && (
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {price.suffix}
            </Typography>
          )}
        </Stack>
        <Box sx={{ minHeight: 20, mt: 0.25 }}>
          {price.sub && (
            <Typography variant="caption" color="text.secondary">
              {price.sub}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={1.25} sx={{ flexGrow: 1 }}>
          {features.slice(0, 6).map((f) => (
            <Stack key={f.label} direction="row" spacing={1.25} alignItems="flex-start">
              <CheckCircle sx={{ fontSize: 18, color: 'success.main', mt: '2px' }} />
              <Box>
                <Typography variant="body2" fontWeight={600} lineHeight={1.3}>
                  {f.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {f.value}
                </Typography>
              </Box>
            </Stack>
          ))}
          {features.length > 6 && (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 0.25 }}>
              +{features.length - 6} more benefits included
            </Typography>
          )}
        </Stack>

        <Box mt={2.5}>
          <Button
            fullWidth
            size="large"
            disableElevation
            variant={isCurrent ? 'outlined' : isUpgrade ? 'contained' : 'outlined'}
            color={isCurrent ? 'primary' : isUpgrade ? 'primary' : 'inherit'}
            disabled={isCurrent || !canPay || isPaying}
            startIcon={isCurrent ? <CheckCircleOutline /> : undefined}
            onClick={() => {
              if (!canPay) return;
              onUpgrade();
            }}
            sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', py: 1.1 }}
          >
            {isCurrent
              ? 'Current plan'
              : isPaying
                ? 'Opening checkout…'
                : isUpgrade
                  ? 'Upgrade'
                  : 'Not available'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const BillingActivePlan: React.FC = () => {
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activePlan, setActivePlan] = useState<SubscriptionPlan | null>(null);
  const [otherPlans, setOtherPlans] = useState<SubscriptionPlan[]>([]);
  const [usage, setUsage] = useState<BillingUsageStatus | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<{
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    renewalCount: number;
    status: 'active' | 'expired' | 'none';
  } | null>(null);

  const [transactions, setTransactions] = useState<BillingTransactionRecord[]>([]);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsMeta, setTransactionsMeta] = useState<{ total: number; hasNext: boolean; hasPrev: boolean } | null>(
    null
  );
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const TRANSACTIONS_PAGE_SIZE = 10;

  const [payingPlan, setPayingPlan] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  // Deep-linkable tabs. Other surfaces (e.g. the storage-full placeholder in a
  // chat bubble) link straight to "compare plans", so the tab must be
  // addressable by URL — landing on Usage instead would silently drop the
  // user one click short of what the link promised.
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingTab, setBillingTab] = useState(() => {
    const i = TAB_SLUGS.indexOf(searchParams.get('tab') ?? '');
    return i >= 0 ? i : 0;
  });

  // Keep the URL in step so the tab survives a refresh or a shared link.
  const selectTab = (next: number) => {
    setBillingTab(next);
    const slug = TAB_SLUGS[next];
    const params = new URLSearchParams(searchParams);
    if (next === 0 || !slug) params.delete('tab');
    else params.set('tab', slug);
    setSearchParams(params, { replace: true });
  };

  // ── WhatsApp messages tab ──
  // Empty string means "don't send this param" — the server then resolves the
  // window itself (billing period, else last 30 days), which is the default
  // the tab opens with. Kept as two flat strings rather than one object so
  // the fetch effect's deps compare by value; an object literal would be a
  // fresh reference every render and refetch forever.
  const [waStartDate, setWaStartDate] = useState('');
  const [waEndDate, setWaEndDate] = useState('');
  const [waPreset, setWaPreset] = useState<'default' | 'cycle' | '7d' | '30d' | 'custom'>(
    'default'
  );
  const [waUsage, setWaUsage] = useState<WhatsAppMessageUsage | null>(null);
  const [waLoading, setWaLoading] = useState(false);
  const [waError, setWaError] = useState<string | null>(null);
  // "Verify with Meta" — on demand only; cleared whenever the report range changes
  // so a result never sits next to numbers it wasn't computed for.
  const [waVerify, setWaVerify] = useState<WhatsAppUsageVerification | null>(null);
  const [waVerifying, setWaVerifying] = useState(false);
  const [waVerifyError, setWaVerifyError] = useState<string | null>(null);
  // Last range we successfully loaded, so flipping away to another tab and
  // back doesn't re-hit the API for data we already hold. Reset to null on
  // failure (and on an ignored in-flight response) so the next visit retries.
  const waLoadedRangeRef = useRef<string | null>(null);

  // Cashfree requires a real contact phone number (UPI prefill, OTP/3DS
  // fallback, fraud-risk scoring) — this prompt only appears the first time,
  // when the signed-in admin has none on file; the backend persists it after.
  const [phonePromptPlan, setPhonePromptPlan] = useState<SubscriptionPlan | null>(null);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Separate from `error`: `error` gates the page's early-return (nothing else
  // rendered but the alert) and is reserved for "we couldn't load your plan
  // data at all". A checkout/confirm failure happens AFTER the page has
  // already loaded successfully — reusing `error` for it would wipe out the
  // whole plans view the user is looking at. `checkoutError` renders inline
  // instead, the same way `success` already does.
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const res = await adminApi.getBillingActivePlan();
      setActivePlan(res.activePlan);
      setOtherPlans(res.otherPlans);
      setUsage(res.usage);
      setBillingPeriod(res.tenant.billing);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to load your active plan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchTransactions = async (page: number) => {
    try {
      setTransactionsLoading(true);
      const res = await adminApi.listBillingTransactions({ page, limit: TRANSACTIONS_PAGE_SIZE });
      setTransactions(res.data);
      setTransactionsMeta({ total: res.meta.total, hasNext: res.meta.hasNext, hasPrev: res.meta.hasPrev });
    } catch {
      // Non-critical section — a failure here shouldn't disturb the rest of the page.
    } finally {
      setTransactionsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(transactionsPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionsPage]);

  // Lazy: nothing is fetched until the WhatsApp tab is actually opened, then
  // again whenever the range changes. Most visitors never open this tab, and
  // the aggregation behind it is the most expensive call on the page.
  useEffect(() => {
    if (billingTab !== WHATSAPP_TAB_INDEX) return;

    const rangeKey = `${waStartDate}|${waEndDate}`;
    if (waLoadedRangeRef.current === rangeKey) return;
    waLoadedRangeRef.current = rangeKey;

    let mounted = true;
    const fetchWhatsAppUsage = async () => {
      try {
        setWaLoading(true);
        setWaError(null);
        const result = await adminApi.getWhatsAppMessageUsage({
          ...(waStartDate ? { startDate: waStartDate } : {}),
          ...(waEndDate ? { endDate: waEndDate } : {}),
        });
        if (!mounted) {
          // Tab switched (or the page unmounted) mid-flight: drop the response
          // and forget the key, otherwise coming back would show nothing and
          // never retry. Only clear it if a newer fetch hasn't already claimed
          // the slot — otherwise this late loser would un-mark the winner.
          if (waLoadedRangeRef.current === rangeKey) waLoadedRangeRef.current = null;
          return;
        }
        setWaUsage(result);
        setWaVerify(null);
        setWaVerifyError(null);
      } catch (err: any) {
        if (waLoadedRangeRef.current === rangeKey) waLoadedRangeRef.current = null;
        if (!mounted) return;
        setWaError(
          err?.response?.data?.error?.message ||
            err?.message ||
            'Failed to load WhatsApp message usage'
        );
      } finally {
        if (mounted) setWaLoading(false);
      }
    };

    void fetchWhatsAppUsage();
    return () => {
      mounted = false;
    };
  }, [billingTab, waStartDate, waEndDate]);

  // Handles the browser's return from Cashfree's hosted checkout — the
  // return_url we configured always carries ?order_id=<gatewayOrderId>. The
  // server-side confirm call (not this redirect itself) is the only thing
  // that decides whether the payment actually succeeded; if the webhook
  // hasn't landed yet, we poll a few times with a fixed delay before giving
  // up with a "we'll confirm shortly" message rather than blocking forever.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gatewayOrderId = params.get(CHECKOUT_ORDER_ID_PARAM);
    if (!gatewayOrderId) return;

    // Strip the param immediately so a page refresh doesn't re-trigger confirm.
    params.delete(CHECKOUT_ORDER_ID_PARAM);
    const newSearch = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));

    let cancelled = false;
    const pollConfirm = async () => {
      for (let attempt = 1; attempt <= CONFIRM_POLL_ATTEMPTS; attempt++) {
        if (cancelled) return;
        try {
          const result = await adminApi.confirmCheckoutOrder(gatewayOrderId);
          const status = result.transaction?.status;
          if (status === 'captured') {
            setCheckoutError(null);
            setSuccess('Payment received successfully and plan updated.');
            await Promise.all([fetchPlans(), refreshUser(), fetchTransactions(1)]);
            setTransactionsPage(1);
            return;
          }
          if (status === 'failed' || status === 'expired') {
            setCheckoutError(
              result.transaction?.failureReason
                ? `Payment ${status}: ${result.transaction.failureReason}`
                : `Payment ${status}. Please try again.`
            );
            return;
          }
          // Still pending (created/pending, or webhook not yet processed) — wait and retry.
        } catch {
          // Transient error confirming — keep polling until attempts are exhausted.
        }
        if (attempt < CONFIRM_POLL_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, CONFIRM_POLL_DELAY_MS));
        }
      }
      if (!cancelled) {
        setSuccess("We're still confirming your payment — this can take a minute. Refresh shortly if your plan hasn't updated.");
      }
    };

    void pollConfirm();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const comparePlanLevel = (planA: SubscriptionPlan, planB: SubscriptionPlan): number => {
    if (planA.sortOrder !== planB.sortOrder) return planA.sortOrder - planB.sortOrder;
    return planA.price - planB.price;
  };

  // Full plan list for the comparison grid — the active plan is shown INLINE with the
  // others (highlighted, not dimmed), the industry-standard "current plan" treatment.
  const allPlans = useMemo(() => {
    const byName = new Map<string, SubscriptionPlan>();
    [activePlan, ...otherPlans].forEach((plan) => {
      if (plan) byName.set(plan.name, plan);
    });
    return Array.from(byName.values()).sort(comparePlanLevel);
  }, [activePlan, otherPlans]);

  const getPlanChangeAction = (targetPlan: SubscriptionPlan): 'upgrade' | 'downgrade' | 'current' => {
    if (!activePlan) return 'current';
    if (targetPlan.name === activePlan.name) return 'current';
    const levelDelta = comparePlanLevel(targetPlan, activePlan);
    if (levelDelta > 0) return 'upgrade';
    if (levelDelta < 0) return 'downgrade';
    return 'current';
  };

  // ── WhatsApp tab derivations ──

  const waCycleStart = isoToDateInput(billingPeriod?.currentPeriodStart);
  const waCycleEnd = isoToDateInput(billingPeriod?.currentPeriodEnd);
  const waCycleAvailable = !!waCycleStart && !!waCycleEnd;

  const applyWaPreset = (preset: 'cycle' | '7d' | '30d') => {
    if (preset === 'cycle') {
      if (!waCycleAvailable) return;
      setWaPreset('cycle');
      setWaStartDate(waCycleStart);
      setWaEndDate(waCycleEnd);
      return;
    }
    const days = preset === '7d' ? 7 : 30;
    setWaPreset(preset);
    setWaStartDate(daysAgoDateInput(days));
    setWaEndDate(toDateInput(new Date()));
  };

  // The API returns days ascending; the table shows the most recent first,
  // matching Billing History right above it.
  const waDaysNewestFirst = useMemo(() => (waUsage ? [...waUsage.byDay].reverse() : []), [waUsage]);

  /** Human label for whatever window the server actually answered with — which
   *  is not necessarily what we asked for, since omitting the params hands the
   *  choice to the server. */
  const waRangeLabel = (() => {
    if (!waUsage) return '';
    const { startDate, endDate, source } = waUsage.range;
    const span = `${formatDayShort(startDate)} – ${formatDayShort(endDate)}`;
    if (source === 'billing_period') return `Current billing cycle · ${span}`;
    if (source === 'default_30d') return `Last 30 days · ${span}`;
    return span;
  })();

  /** A count, or an em dash while there is nothing trustworthy to show. */
  const waStat = (value: number | undefined): string =>
    waLoading || !waUsage || value === undefined ? '—' : integerFormatter(value);

  // b845edd (2026-08-30) is when billing attribution (metadata.template /
  // metadata.metaPricing) started being captured — messages before it have
  // neither, so they under-report as service/non-billable regardless of what
  // they actually were. Safe to delete once the historical backfill has run
  // and enough time has passed that no default range crosses it.
  const WA_BILLING_ATTRIBUTION_START = '2026-08-30';
  const waRangeCrossesAttributionCliff =
    !!waUsage && waUsage.range.startDate < WA_BILLING_ATTRIBUTION_START;

  const verifyWhatsAppWithMeta = async () => {
    if (!waUsage) return;
    setWaVerifying(true);
    setWaVerify(null);
    setWaVerifyError(null);
    try {
      setWaVerify(
        await adminApi.verifyWhatsAppUsageWithMeta({
          startDate: waUsage.range.startDate,
          endDate: waUsage.range.endDate,
        })
      );
    } catch (err: any) {
      setWaVerifyError(err?.response?.data?.error?.message || err?.message || 'Could not reach Meta');
    } finally {
      setWaVerifying(false);
    }
  };

  const exportWhatsAppCsv = () => {
    if (!waUsage) return;
    const header = [
      'Date',
      'Marketing',
      'Utility',
      'Authentication',
      'Service',
      'Other',
      'Total',
      'Charged by Meta',
      'Billed Conversations (CBP)',
    ];
    const rows = waDaysNewestFirst.map((day) => [
      day.date,
      day.marketing,
      day.utility,
      day.authentication,
      day.service,
      day.unknown,
      day.total,
      day.billable,
      day.billedConversations,
    ]);
    const totals = waUsage.totals;
    const footer = [
      'Total',
      totals.marketing,
      totals.utility,
      totals.authentication,
      totals.service,
      totals.unknown,
      totals.total,
      totals.billable,
      totals.billedConversations,
    ];
    // Range-level status breakdown, so an export carries the same
    // free / pending / unconfirmed picture the page shows.
    const summary = [
      [],
      ['Summary'],
      ['Free (confirmed by Meta)', totals.freeCustomerService + totals.freeEntryPoint + totals.freeOther],
      ['  included by Meta', totals.freeCustomerService],
      ['  from ads / page buttons', totals.freeEntryPoint],
      ['Service replies charged', totals.serviceBilled],
      ['Awaiting delivery (not yet charged)', totals.pendingDelivery],
      ['Awaiting Meta confirmation (estimate)', totals.unconfirmed],
    ];
    const csv = [header, ...rows, footer, ...summary].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `whatsapp-messages-${waUsage.range.startDate}-to-${waUsage.range.endDate}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const loadCashfreeScript = async (): Promise<void> => {
    if (window.Cashfree) return;

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Unable to load Cashfree checkout'));
      document.body.appendChild(script);
    });
  };

  // Full-page redirect flow: create the order, then hand off to Cashfree's
  // hosted checkout. There is no in-page success/failure handler here — the
  // browser navigates away and back via return_url, and the `useEffect` above
  // (keyed off ?order_id=) is what resolves the outcome server-side.
  //
  // `phoneNumber` is only passed on the retry after the phone-prompt dialog —
  // the first attempt omits it so the backend's PHONE_NUMBER_REQUIRED check
  // (thrown only when the admin truly has none on file) decides whether the
  // dialog needs to appear at all.
  const openCashfreeCheckout = async (targetPlan: SubscriptionPlan, phoneNumber?: string) => {
    try {
      setPayingPlan(targetPlan.name);
      setCheckoutError(null);
      setSuccess(null);

      await loadCashfreeScript();
      const order = await adminApi.createCheckoutOrder({
        // planId is authoritative when available; planName is only a fallback
        // for the rare case a plan object in hand has no _id.
        ...(targetPlan._id ? { planId: targetPlan._id } : { planName: targetPlan.name }),
        billingCycle,
        ...(phoneNumber ? { phoneNumber } : {}),
      });

      if (!order.paymentSessionId || !window.Cashfree) {
        throw new Error('Unable to start Cashfree checkout');
      }

      const cashfree = window.Cashfree({ mode: CASHFREE_ENV });
      await cashfree.checkout({ paymentSessionId: order.paymentSessionId, redirectTarget: '_self' });
    } catch (err: any) {
      // axios wraps a non-2xx response in its own generic Error whose
      // .message is "Request failed with status code 400" — the backend's
      // actual message lives at response.data.error.message (same shape used
      // throughout the rest of the frontend, e.g. useAnalytics.ts).
      const backendMessage = err?.response?.data?.error?.message;
      if (backendMessage === 'PHONE_NUMBER_REQUIRED') {
        setPayingPlan(null);
        setPhoneInput('');
        setPhoneError(null);
        setPhonePromptPlan(targetPlan);
        return;
      }
      setCheckoutError(backendMessage || err?.message || 'Failed to start payment');
      setPayingPlan(null);
    }
  };

  // Mirrors billing.controller.ts's normalizePhoneNumber — Cashfree expects a
  // bare 10-digit Indian mobile number; strip a +91/91 or 0 prefix so either
  // input style still resolves to what the backend will actually accept.
  const normalizePhoneForCashfree = (raw: string): string | null => {
    let digits = raw.replace(/[^\d]/g, '');
    if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
    else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
    return digits.length === 10 ? digits : null;
  };

  const submitPhoneAndCheckout = async () => {
    const normalized = normalizePhoneForCashfree(phoneInput);
    if (!normalized) {
      setPhoneError('Enter a valid 10-digit mobile number');
      return;
    }
    const targetPlan = phonePromptPlan;
    setPhonePromptPlan(null);
    if (targetPlan) {
      await openCashfreeCheckout(targetPlan, normalized);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" fontWeight="bold" mb={1}>
          Billing &amp; Plans
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!activePlan) {
    return (
      <Box>
        <Typography variant="h4" fontWeight="bold" mb={1}>
          Billing &amp; Plans
        </Typography>
        <Alert severity="warning">No active plan found.</Alert>
      </Box>
    );
  }

  const activePrice = getCyclePrice(activePlan, billingCycle);
  const isFreePlan = activePlan.price <= 0 && (activePlan.yearlyPrice ?? 0) <= 0;

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
      {/* ── Header ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={800}>
            Billing &amp; Plans
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Manage your subscription, track usage, and compare plans.
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={billingCycle}
          onChange={(_, value) => {
            if (value === 'monthly' || value === 'yearly') setBillingCycle(value);
          }}
          sx={{
            bgcolor: 'action.hover',
            borderRadius: 2,
            p: 0.5,
            '& .MuiToggleButton-root': {
              border: 0,
              borderRadius: '8px !important',
              px: 2,
              py: 0.5,
              textTransform: 'none',
              fontWeight: 600,
              '&.Mui-selected': {
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.paper' },
              },
            },
          }}
        >
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="yearly">Yearly</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {checkoutError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setCheckoutError(null)}>
          {checkoutError}
        </Alert>
      )}

      {/* ── Active plan hero ── */}
      <Card
        elevation={0}
        sx={(theme) => ({
          mb: 3,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
            theme.palette.primary.main,
            0.02
          )} 100%)`,
        })}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2.5}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 56,
                  height: 56,
                  borderRadius: 2.5,
                  color: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                })}
              >
                <Diamond />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h5" fontWeight={800}>
                    {activePlan.displayName}
                  </Typography>
                  <Chip
                    size="small"
                    label="Current plan"
                    color="primary"
                    sx={{ fontWeight: 700, height: 22 }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" mt={0.5} sx={{ maxWidth: 440 }}>
                  {activePlan.description}
                </Typography>
                {!isFreePlan && billingPeriod && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                    {billingPeriod.currentPeriodEnd ? (
                      <>
                        Renewed {formatDate(billingPeriod.currentPeriodStart)} · Valid until{' '}
                        <strong>{formatDate(billingPeriod.currentPeriodEnd)}</strong>
                        {billingPeriod.renewalCount > 0 && ` · Renewal #${billingPeriod.renewalCount}`}
                      </>
                    ) : (
                      'Granted by admin — no expiry'
                    )}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Box textAlign={{ xs: 'left', md: 'right' }}>
              <Stack direction="row" alignItems="baseline" spacing={0.75} justifyContent={{ md: 'flex-end' }}>
                <Typography variant="h3" fontWeight={800} lineHeight={1}>
                  {activePrice.amount}
                </Typography>
                {activePrice.suffix && (
                  <Typography variant="body1" color="text.secondary" fontWeight={600}>
                    {activePrice.suffix}
                  </Typography>
                )}
              </Stack>
              {activePrice.sub && (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                  {activePrice.sub}
                </Typography>
              )}
              {isFreePlan ? (
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Upgrade below for more headroom.
                </Typography>
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  disableElevation
                  sx={{ mt: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                  onClick={() => openCashfreeCheckout(activePlan)}
                  disabled={payingPlan === activePlan.name}
                >
                  {payingPlan === activePlan.name ? 'Opening checkout…' : 'Renew plan'}
                </Button>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Usage ── */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={billingTab}
          onChange={(_e, v) => selectTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Usage" />
          <Tab label="Plan Details" />
          <Tab label="Billing History" />
          <Tab label="Compare Plans" />
          <Tab label="WhatsApp Messages" />
        </Tabs>
      </Box>

      {billingTab === 0 && usage && (
        <Card variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h6" fontWeight={800} mb={0.5}>
              Usage this billing cycle
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Live consumption against your current plan limits.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <UsageTile icon={<Forum fontSize="small" />} label="Messages" status={usage.messages} />
              </Grid>
              {/* Hidden for now — commented out, not deleted, per request.
              <Grid item xs={12} sm={6} md={4}>
                <UsageTile
                  icon={<Storage fontSize="small" />}
                  label="Storage"
                  status={usage.storage}
                  formatter={bytesToGB}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <UsageTile icon={<Api fontSize="small" />} label="API calls" status={usage.apiCalls} />
              </Grid>
              */}
              {/* Inbound customer media. Shown (unlike the hidden Storage tile)
                  because when this pool fills, the product visibly changes: new
                  customer attachments keep arriving and are still captured, but
                  only a preview is retained. Without this tile a tenant would
                  see degraded media with no explanation. */}
              {usage.conversationMedia && (
                <Grid item xs={12} sm={6} md={4}>
                  <UsageTile
                    icon={<PermMedia fontSize="small" />}
                    label="Incoming media"
                    status={usage.conversationMedia}
                    formatter={bytesToGB}
                    {...(usage.conversationMedia.exceeded
                      ? {
                          note: 'Full — new attachments are still received and kept as previews, but originals are no longer stored.',
                          // Only surfaced when it is actionable; a link to a
                          // cleanup tool on a healthy meter is just noise.
                          action: { label: 'Free up space', to: '/admin/activity/media' },
                        }
                      : usage.conversationMedia.usagePercent >= 80 && !usage.conversationMedia.unbounded
                        ? {
                            note: 'Nearly full — free up space or raise the allowance to keep storing originals.',
                            action: { label: 'Free up space', to: '/admin/activity/media' },
                          }
                        : {})}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={4}>
                <UsageTile icon={<Group fontSize="small" />} label="Team members" status={usage.users} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <UsageTile icon={<SmartToy fontSize="small" />} label="Agents" status={usage.agents} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <UsageTile icon={<Group fontSize="small" />} label="Contacts" status={usage.contacts} />
              </Grid>
              {/* Hidden for now — commented out, not deleted, per request.
              <Grid item xs={12} sm={6} md={4}>
                <UsageTile icon={<Api fontSize="small" />} label="AI tokens" status={usage.aiTokens} />
              </Grid>
              */}
              <Grid item xs={12} sm={6} md={4}>
                <UsageTile icon={<Group fontSize="small" />} label="Roles" status={usage.roles} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ── Full plan details ── */}
      {billingTab === 1 && (
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="h6" fontWeight={800} mb={0.5}>
            Full plan details
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2.5}>
            Every limit and capability configured for your plan.
          </Typography>

          <CoreFeaturesAccordion features={activePlan.features} />
          {NUMERIC_FEATURE_GROUPS.map((group) => (
            <FeatureGroupAccordion key={group.title} group={group} features={activePlan.features} />
          ))}
        </CardContent>
      </Card>
      )}

      {/* ── Billing history ── */}
      {billingTab === 2 && (transactions.length > 0 || transactionsLoading) && (
        <Card variant="outlined" sx={{ mb: 4, borderRadius: 3, borderColor: '#e8ecf3' }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, '&:last-child': { pb: { xs: 2.5, md: 3.5 } } }}>
            <Stack direction="row" spacing={1.5} alignItems="center" mb={3}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(79, 70, 229, 0.08)',
                  color: '#4f46e5',
                  flexShrink: 0,
                }}
              >
                <Receipt fontSize="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.0625rem', lineHeight: 1.3 }}>
                  Billing history
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Every checkout attempt on your account, most recent first.
                </Typography>
              </Box>
            </Stack>

            {(() => {
              // The "Resolved via" column only means something for real gateway payments
              // (webhook / redirect / sweep). Admin-granted plans never have one, and a column
              // of identical dashes is just noise — so it only appears when some row has a value.
              const showResolvedVia = transactions.some((t) => Boolean(t.resolvedVia));
              return (
                <TableContainer
                  sx={{
                    border: '1px solid #e8ecf3',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    opacity: transactionsLoading ? 0.6 : 1,
                    transition: 'opacity .15s',
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                        {showResolvedVia && <TableCell>Resolved via</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((tx) => {
                        const statusStyle = TRANSACTION_STATUS_CHIP[tx.status];
                        const when = tx.paidAt || tx.createdAt;
                        const noCharge = !tx.amount;
                        return (
                          <TableRow key={tx._id} hover>
                            <TableCell sx={{ py: 1.75 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {formatDay(when)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatClock(when)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.75 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                                {tx.planName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                                {tx.billingCycle}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.75 }}>
                              {noCharge ? (
                                <Tooltip title="No payment was taken for this entry (e.g. a plan granted by your administrator).">
                                  <Typography variant="body2" color="text.disabled" component="span">
                                    No charge
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" fontWeight={600}>
                                  {tx.currency} {formatMoney(tx.amount)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 1.75 }}>
                              <Stack spacing={0.25} alignItems="flex-start">
                                <StatusChip status={statusStyle.tone} label={statusStyle.label} dot />
                                {tx.failureReason && (
                                  <Typography
                                    variant="caption"
                                    color="error.main"
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 1,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      maxWidth: 220,
                                    }}
                                    title={tx.failureReason}
                                  >
                                    {tx.failureReason}
                                  </Typography>
                                )}
                              </Stack>
                            </TableCell>
                            {showResolvedVia && (
                              <TableCell sx={{ py: 1.75 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {tx.resolvedVia ? RESOLVED_VIA_LABELS[tx.resolvedVia] : '—'}
                                </Typography>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })()}

            {transactionsMeta && (transactionsMeta.hasPrev || transactionsMeta.hasNext) && (
              <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} mt={2}>
                <IconButton
                  size="small"
                  disabled={!transactionsMeta.hasPrev || transactionsLoading}
                  onClick={() => setTransactionsPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <Typography variant="caption" color="text.secondary">
                  Page {transactionsPage}
                </Typography>
                <IconButton
                  size="small"
                  disabled={!transactionsMeta.hasNext || transactionsLoading}
                  onClick={() => setTransactionsPage((p) => p + 1)}
                >
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {billingTab === 2 && !transactionsLoading && transactions.length === 0 && (
        <Card variant="outlined" sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 4 }, textAlign: 'center' }}>
            <Receipt color="action" />
            <Typography variant="body2" color="text.secondary" mt={1}>
              No billing history yet. Invoices appear here after your first payment.
            </Typography>
          </CardContent>
        </Card>
      )}

      {billingTab === 3 && (
      <>
      <Box mb={1}>
        <Typography variant="h5" fontWeight={800}>
          Compare plans
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5} mb={3}>
          Pick the plan that fits — your current plan is highlighted below.
        </Typography>
      </Box>

      {allPlans.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No plans are available right now.
        </Typography>
      ) : (
        <Grid container spacing={3} alignItems="stretch">
          {allPlans.map((plan) => {
            const action = getPlanChangeAction(plan);
            return (
              <Grid item xs={12} sm={6} lg={4} key={plan.name}>
                <PlanCompareCard
                  plan={plan}
                  billingCycle={billingCycle}
                  isCurrent={action === 'current'}
                  isUpgrade={action === 'upgrade'}
                  isPaying={payingPlan === plan.name}
                  onUpgrade={() => void openCashfreeCheckout(plan)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
      </>
      )}

      {/* ── WhatsApp messages billed by Meta ── */}
      {billingTab === WHATSAPP_TAB_INDEX && (
        <Card variant="outlined" sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack direction="row" spacing={1.25} alignItems="center" mb={0.5}>
              <Forum fontSize="small" color="action" />
              <Typography variant="h6" fontWeight={800}>
                WhatsApp messages billed by Meta
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Meta charges per delivered message. From 1 Oct 2026 this includes replies to customers,
              after Meta&apos;s monthly free allowance. Every figure below follows the billing status
              Meta itself reports for each message.
            </Typography>
            {waUsage && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                {waRangeLabel}
              </Typography>
            )}
            {waRangeCrossesAttributionCliff && (
              <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
                Category/billable attribution only started on {formatDayShort(WA_BILLING_ATTRIBUTION_START)}.
                Messages before that date are undercounted here — they show as Service even when they
                weren't.
              </Alert>
            )}

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', md: 'center' }}
              flexWrap="wrap"
              useFlexGap
              mt={2.5}
              mb={3}
            >
              <ToggleButtonGroup
                size="small"
                exclusive
                value={waPreset}
                onChange={(_, value) => {
                  // `null` arrives when the selected button is clicked again;
                  // keep the range rather than silently falling back.
                  if (value === 'cycle' || value === '7d' || value === '30d') applyWaPreset(value);
                }}
                sx={{
                  bgcolor: 'action.hover',
                  borderRadius: 2,
                  p: 0.5,
                  alignSelf: { xs: 'flex-start', md: 'center' },
                  '& .MuiToggleButton-root': {
                    border: 0,
                    borderRadius: '8px !important',
                    px: 2,
                    py: 0.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': {
                      bgcolor: 'background.paper',
                      boxShadow: 1,
                      '&:hover': { bgcolor: 'background.paper' },
                    },
                  },
                }}
              >
                {/* Only offered when the page actually knows the cycle bounds —
                    a free/admin-granted plan has none to send. */}
                {waCycleAvailable && <ToggleButton value="cycle">This cycle</ToggleButton>}
                <ToggleButton value="7d">Last 7 days</ToggleButton>
                <ToggleButton value="30d">Last 30 days</ToggleButton>
              </ToggleButtonGroup>

              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={waStartDate}
                  onChange={(e) => {
                    setWaPreset('custom');
                    setWaStartDate(e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 170 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={waEndDate}
                  onChange={(e) => {
                    setWaPreset('custom');
                    setWaEndDate(e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 170 }}
                />
              </Stack>
            </Stack>

            {waError && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {waError}
              </Alert>
            )}

            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6} md={3}>
                <WhatsAppStatCard
                  label="Total sent"
                  value={waStat(waUsage?.totals.total)}
                  caption="All WhatsApp messages in this range"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <WhatsAppStatCard
                  highlight
                  label="Charged by Meta"
                  value={waStat(waUsage?.totals.billable)}
                  caption={
                    waUsage
                      ? [
                          'Delivered and billed',
                          waUsage.totals.serviceBilled
                            ? `${integerFormatter(waUsage.totals.serviceBilled)} are service replies`
                            : '',
                          waUsage.totals.pendingDelivery
                            ? `${integerFormatter(waUsage.totals.pendingDelivery)} more awaiting delivery`
                            : '',
                          waUsage.totals.billedConversations
                            ? 'excludes conversation-based charges'
                            : '',
                        ]
                          .filter(Boolean)
                          .join(' · ')
                      : 'Delivered and billed'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <WhatsAppStatCard
                  label="Free"
                  value={waStat(
                    waUsage
                      ? waUsage.totals.freeCustomerService +
                          waUsage.totals.freeEntryPoint +
                          waUsage.totals.freeOther
                      : undefined
                  )}
                  caption={
                    waUsage
                      ? `${integerFormatter(waUsage.totals.freeCustomerService)} included by Meta · ${integerFormatter(
                          waUsage.totals.freeEntryPoint
                        )} from ads`
                      : 'Confirmed free by Meta'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <WhatsAppStatCard
                  label="Awaiting confirmation"
                  value={waStat(waUsage?.totals.unconfirmed)}
                  caption="No Meta pricing received yet — counted as an estimate"
                />
              </Grid>
              {/* Only shown once there's something to show — most tenants are
                  pure per-message pricing today (Meta has migrated most WABAs
                  off CBP), so this stays invisible noise for the common case. */}
              {!!waUsage?.totals.billedConversations && (
                <Grid item xs={12} sm={6} md={3}>
                  <WhatsAppStatCard
                    label="Billed conversations"
                    value={waStat(waUsage?.totals.billedConversations)}
                    caption="Conversation-based billing — one charge per 24h thread"
                  />
                </Grid>
              )}
            </Grid>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={3}>
              <Chip
                size="small"
                label={`Campaigns ${waStat(waUsage?.bySource.campaign)}`}
                sx={{ height: 24, fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`Automation ${waStat(waUsage?.bySource.automation)}`}
                sx={{ height: 24, fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`Bot replies ${waStat(waUsage?.bySource.bot)}`}
                sx={{ height: 24, fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`Manual ${waStat(waUsage?.bySource.manual)}`}
                sx={{ height: 24, fontWeight: 600 }}
              />
            </Stack>

            {!waError && (
              <>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  mb={1.5}
                >
                  <Typography variant="subtitle2" fontWeight={800}>
                    Daily breakdown
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      disableElevation
                      onClick={verifyWhatsAppWithMeta}
                      disabled={waLoading || !waUsage || waVerifying}
                      startIcon={waVerifying ? <CircularProgress size={14} /> : undefined}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                      Verify with Meta
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disableElevation
                      onClick={exportWhatsAppCsv}
                      disabled={waLoading || !waUsage || waUsage.byDay.length === 0}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                      Export CSV
                    </Button>
                  </Stack>
                </Stack>

                {waVerifyError && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {waVerifyError}
                  </Alert>
                )}
                {waVerify && !waVerify.available && (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    {waVerify.message}
                  </Alert>
                )}
                {waVerify && waVerify.available && (
                  <Alert severity={waVerify.matches ? 'success' : 'warning'} sx={{ mb: 2, borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {waVerify.matches
                        ? `Matches Meta — all ${integerFormatter(waVerify.totals.meta)} delivered messages agree.`
                        : `Differs from Meta — Meta counts ${integerFormatter(waVerify.totals.meta)}, we count ${integerFormatter(
                            waVerify.totals.ours
                          )}.`}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Compared over {formatDayShort(waVerify.window.start.slice(0, 10))} –{' '}
                      {formatDayShort(
                        new Date(new Date(waVerify.window.end).getTime() - 24 * 60 * 60 * 1000)
                          .toISOString()
                          .slice(0, 10)
                      )}{' '}
                      in Meta&apos;s own day boundaries, so edge days can differ slightly from the table below.
                    </Typography>
                    {waVerify.rows
                      .filter((r) => r.diff !== 0)
                      .map((r) => (
                        <Typography key={`${r.category}|${r.type}`} variant="body2" mt={0.5}>
                          {r.category.replace(/_/g, ' ')} ({r.type.replace(/_/g, ' ')}): Meta {integerFormatter(r.meta)},
                          ours {integerFormatter(r.ours)}
                        </Typography>
                      ))}
                    {waVerify.ourUnconfirmed > 0 && (
                      <Typography variant="body2" mt={0.5}>
                        {integerFormatter(waVerify.ourUnconfirmed)} delivered messages have no Meta pricing recorded yet.
                      </Typography>
                    )}
                  </Alert>
                )}

                {waLoading ? (
                  <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Marketing</TableCell>
                          <TableCell align="right">Utility</TableCell>
                          <TableCell align="right">Authentication</TableCell>
                          <TableCell align="right">Service</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Messages whose Meta category is outside marketing / utility / authentication / service (e.g. a category Meta added later). Counted in Total.">
                              <span>Other</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">Charged</TableCell>
                          {/* Same condition as the stat card above — hidden entirely
                              for a pure-PMP tenant/range rather than showing an
                              always-zero column. */}
                          {!!waUsage?.totals.billedConversations && (
                            <TableCell align="right">
                              <Tooltip title="Conversation-window billing (CBP) — one charge per 24h thread, not per message. Separate from Billable, which counts messages.">
                                <span>Billed Conv.</span>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {waDaysNewestFirst.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={waUsage?.totals.billedConversations ? 8 : 7}
                              align="center"
                              sx={{ py: 4, border: 0 }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                No WhatsApp messages sent in this range
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          <>
                            {waDaysNewestFirst.map((day) => (
                              <TableRow key={day.date} hover>
                                <TableCell>
                                  <Typography variant="body2">{formatDayLong(day.date)}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  {integerFormatter(day.marketing)}
                                </TableCell>
                                <TableCell align="right">{integerFormatter(day.utility)}</TableCell>
                                <TableCell align="right">
                                  {integerFormatter(day.authentication)}
                                </TableCell>
                                <TableCell align="right">{integerFormatter(day.service)}</TableCell>
                                <TableCell align="right">{integerFormatter(day.unknown)}</TableCell>
                                <TableCell align="right">{integerFormatter(day.total)}</TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={700}>
                                    {integerFormatter(day.billable)}
                                  </Typography>
                                </TableCell>
                                {!!waUsage?.totals.billedConversations && (
                                  <TableCell align="right">
                                    {integerFormatter(day.billedConversations)}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                            <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 800 } }}>
                              <TableCell>Total</TableCell>
                              <TableCell align="right">
                                {waStat(waUsage?.totals.marketing)}
                              </TableCell>
                              <TableCell align="right">{waStat(waUsage?.totals.utility)}</TableCell>
                              <TableCell align="right">
                                {waStat(waUsage?.totals.authentication)}
                              </TableCell>
                              <TableCell align="right">{waStat(waUsage?.totals.service)}</TableCell>
                              <TableCell align="right">{waStat(waUsage?.totals.unknown)}</TableCell>
                              <TableCell align="right">{waStat(waUsage?.totals.total)}</TableCell>
                              <TableCell align="right">
                                {waStat(waUsage?.totals.billable)}
                              </TableCell>
                              {!!waUsage?.totals.billedConversations && (
                                <TableCell align="right">
                                  {waStat(waUsage?.totals.billedConversations)}
                                </TableCell>
                              )}
                            </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!phonePromptPlan} onClose={() => setPhonePromptPlan(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Contact number required</DialogTitle>
        <DialogContent>
          <DialogContentText mb={2}>
            Cashfree needs a phone number to process your payment securely (used for payment verification only).
            You'll only be asked for this once.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Phone number"
            value={phoneInput}
            onChange={(e) => {
              setPhoneInput(e.target.value);
              setPhoneError(null);
            }}
            error={!!phoneError}
            helperText={phoneError || undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submitPhoneAndCheckout();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhonePromptPlan(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => void submitPhoneAndCheckout()}>
            Continue to payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingActivePlan;
